// Projectiles of unit and structure attacks (GDD §8, docs/ART-DIRECTION.md §7): glowing streaks flown along
// procedural trajectories, one ribbon slot each in the shared RibbonPool. A projectile's head runs its trajectory over
// the flight and a streak trails it; on landing it plays its impact effects and shrinks into the landing point. Each
// trajectory is one table entry: how high it rides over the straight path, how fast it covers the ground, and whether
// it lands the instant it fires (beams).
//   bolt   — flat: straight tracer fire.
//   shell  — parabolic lift 4s(1 − s): cannon shells and artillery lobs (the def sets the apex).
//   homing — climbs first (the ground covered goes as s^1.6) and dives on the target, snaking sideways along a
//            decaying sine sin(πs)·sin(2πfs + φ) with per-launch amplitude, frequency and phase.
//   bomb   — falls from the shooter's altitude with a gravity-like 1 − s² drop while drifting to its landing point.
//   beam   — the whole line at once, landing instantly (lasers).
// Horizontal motion is a great-circle slerp between the ends and height is added on top, so paths hug the globe at any
// range. Slots recycle oldest first; nothing allocates per frame.

import { Vector3, type Color } from 'three'
import { InvariantViolation, NEUTRAL_OWNER, type OwnerValue } from '@taxwar/net'
import type { EffectIdFor } from './effects'
import type { ColourKey } from './colours'
import { angleBetween, MIN_PATH_ANGLE } from './paths'
import { RIBBON_POINTS, RIBBON_SEGMENTS, type RibbonPool } from './RibbonPool'

export const TRAJECTORIES = ['bolt', 'shell', 'homing', 'bomb', 'beam'] as const
export type Trajectory = (typeof TRAJECTORIES)[number]

/** How a projectile looks and flies. Widths are CSS pixels (scaled per frame like the glyphs). */
export interface ProjectileDef {
  readonly trajectory: Trajectory
  /** Flight seconds per render unit of distance, clamped to [minSeconds, maxSeconds]; a beam holds for minSeconds. */
  readonly secondsPerUnit: number
  readonly minSeconds: number
  readonly maxSeconds: number
  /** Visible length behind the head, as a fraction of the trajectory (a beam shows all of it). */
  readonly streak: number
  /** Peak height over the straight path, as a fraction of its length (shells and homing climbs). */
  readonly lift: number
  readonly widthPx: number
  /** 0..1 white-hot centre line. */
  readonly core: number
  /** Dashes per render unit along the streak (0 = solid). */
  readonly dashesPerUnit: number
  readonly colour: ColourKey
  readonly intensity: number
  /** Seconds the streak takes to shrink into its landing point. */
  readonly fade: number
  /** Effects played at the landing point. */
  readonly impact: readonly EffectIdFor<'point'>[]
}

/** One launch, filled by the attack styles and copied by launch(); reuse it. */
export class Launch {
  def: ProjectileDef | null = null
  owner: OwnerValue = NEUTRAL_OWNER
  colour: Color | null = null
  /** World positions of the muzzle and the landing point. */
  readonly from = new Vector3()
  readonly to = new Vector3()
  delay = 0
  /** Homing snake: amplitude (fraction of the path length), cycles over the flight, phase in radians. */
  wobble = 0
  wobbleCycles = 0
  wobblePhase = 0
}

/** Where a projectile landed (valid during the call), for its impact effects. */
export type ImpactListener = (def: ProjectileDef, owner: OwnerValue, at: Vector3) => void

interface TrajectoryShape {
  /** Height over the straight path at s ∈ [0, 1], as a fraction of the def's peak lift. */
  readonly rise: (s: number) => number
  /** Fraction of the ground covered at s (1 at s = 1). */
  readonly ground: (s: number) => number
  /** Lands the instant it fires: the head is at the target for the whole life. */
  readonly instant: boolean
}

const HOMING_GROUND_EXPONENT = 1.6
const HOMING_RISE_EXPONENT = 0.6
/** Streak brightness at its tail relative to its head. */
const TAIL_ALPHA = 0.12
const XYZ = 3
const TAU = Math.PI * 2

const linear = (s: number): number => s
const flat = (): number => 0

const TRAJECTORY_SHAPES: Readonly<Record<Trajectory, TrajectoryShape>> = {
  bolt: { rise: flat, ground: linear, instant: false },
  shell: { rise: (s) => 4 * s * (1 - s), ground: linear, instant: false },
  homing: { rise: (s) => Math.pow(Math.sin(Math.PI * s), HOMING_RISE_EXPONENT), ground: (s) => Math.pow(s, HOMING_GROUND_EXPONENT), instant: false },
  bomb: { rise: flat, ground: linear, instant: false },
  beam: { rise: flat, ground: linear, instant: true },
}

export class ProjectileSystem {
  private readonly defs: (ProjectileDef | null)[]
  private readonly owners: OwnerValue[]
  private readonly colours: (Color | null)[]
  private readonly starts: Float64Array
  private readonly flights: Float32Array
  private readonly landed: Uint8Array
  /** Unit directions and radii of both ends, the angle between them and the straight-path length. */
  private readonly fromDirs: Vector3[]
  private readonly toDirs: Vector3[]
  private readonly fromRadii: Float32Array
  private readonly toRadii: Float32Array
  private readonly angles: Float32Array
  private readonly lengths: Float32Array
  /** Homing snake: sideways axis (unit, tangent to the path) and its sine parameters. */
  private readonly sideways: Vector3[]
  private readonly wobbles: Float32Array
  private readonly wobbleCycles: Float32Array
  private readonly wobblePhases: Float32Array
  private cursor = 0

  // Per-frame scratch.
  private readonly points = new Float32Array(RIBBON_POINTS * XYZ)
  private readonly sample = new Vector3()
  private readonly landing = new Vector3()
  private readonly style = { width: 0, dashesPerUnit: 0, dashDuty: 0.6, scroll: 0, core: 0 }

  /** Uses ribbon slots [firstSlot, firstSlot + capacity). */
  constructor(
    private readonly ribbons: RibbonPool,
    private readonly firstSlot: number,
    private readonly capacity: number,
    private readonly onImpact: ImpactListener,
  ) {
    if (firstSlot + capacity > ribbons.capacity) throw new InvariantViolation('projectile slots overflow the ribbon pool')
    this.defs = new Array<ProjectileDef | null>(capacity).fill(null)
    this.owners = new Array<OwnerValue>(capacity).fill(NEUTRAL_OWNER)
    this.colours = new Array<Color | null>(capacity).fill(null)
    this.starts = new Float64Array(capacity)
    this.flights = new Float32Array(capacity)
    this.landed = new Uint8Array(capacity)
    this.fromDirs = Array.from({ length: capacity }, () => new Vector3())
    this.toDirs = Array.from({ length: capacity }, () => new Vector3())
    this.fromRadii = new Float32Array(capacity)
    this.toRadii = new Float32Array(capacity)
    this.angles = new Float32Array(capacity)
    this.lengths = new Float32Array(capacity)
    this.sideways = Array.from({ length: capacity }, () => new Vector3())
    this.wobbles = new Float32Array(capacity)
    this.wobbleCycles = new Float32Array(capacity)
    this.wobblePhases = new Float32Array(capacity)
  }

  /** Starts `launch` at layer time `now` (plus its delay); the oldest projectile gives up its slot when all are busy. */
  launch(launch: Launch, now: number): void {
    const def = launch.def
    const colour = launch.colour
    if (def === null || colour === null) throw new InvariantViolation('projectile launched without a def or a colour')
    const index = this.cursor
    this.cursor = (this.cursor + 1) % this.capacity
    const fromRadius = launch.from.length()
    const toRadius = launch.to.length()
    const fromDir = (this.fromDirs[index] as Vector3).copy(launch.from).divideScalar(fromRadius)
    const toDir = (this.toDirs[index] as Vector3).copy(launch.to).divideScalar(toRadius)
    const length = launch.from.distanceTo(launch.to)
    this.defs[index] = def
    this.owners[index] = launch.owner
    this.colours[index] = colour
    this.starts[index] = now + launch.delay
    this.flights[index] = TRAJECTORY_SHAPES[def.trajectory].instant ? def.minSeconds : clamp(length * def.secondsPerUnit, def.minSeconds, def.maxSeconds)
    this.landed[index] = 0
    this.fromRadii[index] = fromRadius
    this.toRadii[index] = toRadius
    this.angles[index] = angleBetween(fromDir, toDir)
    this.lengths[index] = length
    // Sideways = path direction × local up, in the tangent plane halfway along.
    const side = (this.sideways[index] as Vector3).copy(launch.to).sub(launch.from).cross(this.sample.copy(fromDir).add(toDir))
    if (side.lengthSq() > 0) side.normalize()
    this.wobbles[index] = launch.wobble
    this.wobbleCycles[index] = launch.wobbleCycles
    this.wobblePhases[index] = launch.wobblePhase
    this.ribbons.hide(this.firstSlot + index)
  }

  /** `worldPerCssPixel` turns the defs' pixel widths into render units this frame. */
  update(now: number, worldPerCssPixel: number): void {
    for (let index = 0; index < this.capacity; index++) {
      const def = this.defs[index]
      if (def === null || def === undefined) continue
      const age = now - (this.starts[index] as number)
      if (age < 0) continue
      const shape = TRAJECTORY_SHAPES[def.trajectory]
      const flight = this.flights[index] as number
      const landsAt = shape.instant ? 0 : flight
      if (this.landed[index] === 0 && age >= landsAt) {
        this.landed[index] = 1
        this.onImpact(def, this.owners[index] as OwnerValue, this.pointAt(index, 1, this.landing))
      }
      const life = flight + def.fade
      const slot = this.firstSlot + index
      if (age >= life) {
        this.ribbons.hide(slot)
        this.defs[index] = null
        this.colours[index] = null
        continue
      }
      let head: number
      let tail: number
      let brightness: number
      if (shape.instant) {
        head = 1
        tail = 0
        brightness = 1 - age / life
      } else if (age < flight) {
        head = age / flight
        tail = Math.max(0, head - def.streak)
        brightness = 1
      } else {
        // Landed: the streak shrinks into the landing point.
        const fade = (age - flight) / def.fade
        head = 1
        tail = Math.min(1, 1 - def.streak * (1 - fade))
        brightness = 1 - fade
      }
      if (head <= tail) {
        this.ribbons.hide(slot)
        continue
      }
      for (let i = 0; i < RIBBON_POINTS; i++) this.pointAt(index, tail + ((head - tail) * i) / RIBBON_SEGMENTS, this.sample).toArray(this.points, i * XYZ)
      const style = this.style
      style.width = def.widthPx * worldPerCssPixel
      style.dashesPerUnit = def.dashesPerUnit
      style.core = def.core
      this.ribbons.write(slot, this.points, style, this.colours[index] as Color, def.intensity, brightness * TAIL_ALPHA, brightness, 0)
    }
  }

  /** The point at trajectory fraction `s` of projectile `index`. */
  private pointAt(index: number, s: number, out: Vector3): Vector3 {
    const def = this.defs[index] as ProjectileDef
    const shape = TRAJECTORY_SHAPES[def.trajectory]
    const fromDir = this.fromDirs[index] as Vector3
    const toDir = this.toDirs[index] as Vector3
    const angle = this.angles[index] as number
    const along = shape.ground(s)
    if (angle < MIN_PATH_ANGLE) {
      out.copy(fromDir).lerp(toDir, along).normalize()
    } else {
      const sinAngle = Math.sin(angle)
      out.copy(fromDir).multiplyScalar(Math.sin((1 - along) * angle) / sinAngle).addScaledVector(toDir, Math.sin(along * angle) / sinAngle)
    }
    const fromRadius = this.fromRadii[index] as number
    const toRadius = this.toRadii[index] as number
    const length = this.lengths[index] as number
    const radius = RADIUS_PROFILES[def.trajectory](s, fromRadius, toRadius) + shape.rise(s) * def.lift * length
    out.multiplyScalar(radius)
    const wobble = this.wobbles[index] as number
    if (wobble !== 0) {
      const snake = wobble * length * Math.sin(Math.PI * s) * Math.sin(TAU * (this.wobbleCycles[index] as number) * s + (this.wobblePhases[index] as number))
      out.addScaledVector(this.sideways[index] as Vector3, snake)
    }
    return out
  }
}

/** Height of the straight path between the ends; bombs fall with gravity instead of descending evenly. */
const RADIUS_PROFILES: Readonly<Record<Trajectory, (s: number, from: number, to: number) => number>> = {
  bolt: (s, from, to) => from + (to - from) * s,
  shell: (s, from, to) => from + (to - from) * s,
  homing: (s, from, to) => from + (to - from) * s,
  bomb: (s, from, to) => to + (from - to) * (1 - s * s),
  beam: (s, from, to) => from + (to - from) * s,
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}
